
import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { NAV_ITEMS } from '@/utils/navigation';

interface NavigationProps {
  isHidden?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({ isHidden = false }) => {
  const { user } = useAuth(); // Get the user from AuthContext
  const location = useLocation();
  // ---- desktop sidebar state ----
  const [isOpen, setIsOpen] = React.useState(true);

  /* keep the rest of the app in‑sync with the sidebar's width */
  useEffect(() => {
    // full width = 16 rem; collapsed rail (after -translate-x-56) ≈ 2 rem
    document.documentElement.style.setProperty(
      '--sidebar-width',
      isOpen ? '16rem' : '2rem'
    );
    document.documentElement.style.setProperty(
      '--sidebar-expanded',
      isOpen ? '1' : '0'
    );
    window.dispatchEvent(new Event('sidebar-toggled'));
  }, [isOpen]);

  return (
    <>
      {user && (
        <>
          <nav
            data-testid="mobile-navigation"
            className={cn(
              "md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-[100] px-2 py-2 h-16 pointer-events-auto transform-gpu",
              "transition-transform duration-300 ease-in-out",
              isHidden ? "translate-y-full" : "translate-y-0"
            )}
          >
            <div className="w-full grid grid-cols-6 gap-0">
              {NAV_ITEMS.map((item) => {
                if (item.disabled) {
                  return (
                    <div
                      key={item.path}
                      className="flex flex-col items-center p-1 rounded-lg text-muted-foreground opacity-50 cursor-not-allowed"
                    >
                      <item.icon className="w-5 h-5 mb-1" />
                      <span className="text-[10px] font-medium leading-tight text-center">{item.label}</span>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={!item.children}
                    className={({ isActive }) => `
                      flex flex-col items-center p-1 rounded-lg transition-all
                      ${isActive
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground'}
                    `}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className={`w-5 h-5 mb-1 ${isActive ? 'animate-scale-in' : ''}`} />
                        <span className="text-[10px] font-medium leading-tight text-center">{item.label}</span>
                        {isActive && (
                          <span className="absolute -bottom-2 h-1 w-6 bg-primary rounded-full" />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>

            {/* ---------- mobile profile sub-menu ---------- */}
            {NAV_ITEMS.map(
              (item) =>
                item.children &&
                !item.disabled && // Ensure parent item is not disabled for sub-menu
                location.pathname.startsWith(item.path) && (
                  <div
                    key={`${item.path}-mobile-sub`}
                    className="grid grid-cols-3 gap-1 mt-2"
                  >
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        end={child.path === '/profile'} // Add end prop for exact match on the root profile path
                        className={({ isActive }) =>
                          `text-[11px] px-2 py-1 rounded-md ${
                            isActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:text-foreground'
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )
            )}
          </nav>

          <aside
            data-testid="desktop-navigation"
            className={cn(
              "hidden md:flex flex-col fixed left-0 top-0 h-full bg-background border-r border-border z-50 w-64 px-4 py-6 space-y-4",
              "transition-transform duration-300",
              isHidden ? "-translate-x-full" : (isOpen ? "translate-x-0" : "-translate-x-56")
            )}
          >
            {/* slide-toggle button */}
            <button
              onClick={() => setIsOpen(p => !p)}
              className="absolute top-1/2 -right-3 -translate-y-1/2 p-1 rounded-full
                         bg-background border border-border shadow-md
                         hover:bg-accent transition-colors"
              aria-label={isOpen ? "Collapse navigation" : "Expand navigation"}
            >
              {isOpen ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            <div className="flex flex-col space-y-2">
              {NAV_ITEMS.map((item) => {
                if (item.disabled) {
                  return (
                    <div
                      key={item.path}
                      className="flex items-center p-3 rounded-lg text-muted-foreground opacity-50 cursor-not-allowed"
                    >
                      <item.icon className="w-5 h-5 mr-3" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={!item.children}
                    className={({ isActive }) =>
                      `flex items-center p-3 rounded-lg transition-all ${
                        isActive
                          ? 'text-primary bg-primary/10'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className="w-5 h-5 mr-3" />
                        <span className="text-sm font-medium">{item.label}</span>
                        {isActive && (
                          <span className="ml-auto h-2 w-2 bg-led-cyan rounded-full shadow-[0_0_5px_rgba(110,231,255,0.5)]" />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}

              {/* ---------- desktop profile sub-menu ---------- */}
              {NAV_ITEMS.map((item) =>
                item.children &&
                !item.disabled && // Ensure parent item is not disabled for sub-menu
                location.pathname.startsWith(item.path) && (
                  <div key={`${item.path}-desktop-sub`} className="flex flex-col ml-8 mt-2 space-y-1">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        end={child.path === '/profile'}
                        className={({ isActive }) =>
                          `text-sm rounded-md px-2 py-1 ${
                            isActive
                              ? 'text-primary'
                              : 'text-muted-foreground hover:text-foreground'
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
};
